import React from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Home, MessageSquare, Compass, User } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";

export default function MobileBottomNav() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();

  // 🧘 Guest Support: Allow guests to use the bottom nav to roam the sanctuary
  const hidePaths = ["/login", "/signup", "/onboarding", "/verify-pending"];
  const shouldHide = hidePaths.some(p => location.pathname.startsWith(p));
  
  if (shouldHide || location.pathname === "/") return null;

  const getActiveTab = () => {
    if (location.pathname.startsWith("/chat")) return "chats";
    if (location.pathname.startsWith("/explore") || location.pathname.startsWith("/article")) return "wisdom";
    if (location.pathname.startsWith("/profile") || (location.pathname === "/dashboard" && location.search.includes("tab=profile"))) return "profile";
    return "home"; // default to home/dashboard
  };

  const activeTab = getActiveTab();

  const tabs = [
    { id: "home", label: t('nav_home'), icon: Home, to: "/dashboard" },
    { id: "chats", label: t('nav_chats_tab'), icon: MessageSquare, to: user ? "/chat" : "/signup" },
    { id: "wisdom", label: t('nav_wisdom_tab'), icon: Compass, to: "/explore" },
    { id: "profile", label: t('nav_profile_tab'), icon: User, to: user ? "/profile" : "/signup" },
  ];

  return (
    <>
      {/* Spacer to prevent content from hiding behind the bottom nav */}
      <div className="h-20 w-full md:hidden flex-shrink-0" />
      
      {/* The Bottom Nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-[100] bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl border-t border-gray-200 dark:border-gray-800 pb-[env(safe-area-inset-bottom)]">
        <div className="flex items-center justify-around h-16 px-2">
          {tabs.map((tab, idx) => {
            const isActive = activeTab === tab.id;
            const Icon = tab.icon;
            
            return (
              <React.Fragment key={tab.id}>
                {idx === 2 && (
                  /* Central Placeholder for Sanctuary FAB */
                  <div className="w-[72px] h-full flex-shrink-0" aria-hidden="true" />
                )}
                <Link 
                  to={tab.to}
                  className="relative flex flex-col items-center justify-center w-full h-full space-y-1"
                  aria-label={tab.label}
                >
                  <motion.div
                    animate={{ 
                      y: isActive ? -4 : 0,
                      scale: isActive ? 1.1 : 1 
                    }}
                    transition={{ 
                      duration: 0.6,
                      ease: [0.22, 1, 0.36, 1] 
                    }}
                    className={`p-1.5 rounded-full ${isActive ? 'text-brand-blue' : 'text-gray-500 dark:text-gray-400'}`}
                  >
                    <Icon className="w-6 h-6 stroke-[1.5px]" />
                  </motion.div>
                  
                  <span className={`text-[10px] font-medium transition-colors ${isActive ? 'text-brand-blue opacity-100' : 'text-gray-500 dark:text-gray-400 opacity-80'}`}>
                    {tab.label}
                  </span>

                  {isActive && (
                    <motion.div 
                      layoutId="activeTabDot"
                      className="absolute -top-px left-1/2 -translate-x-1/2 w-8 h-1 rounded-b-full bg-brand-blue shadow-[0_2px_8px_rgba(59,130,246,0.6)]"
                    />
                  )}
                </Link>
              </React.Fragment>
            );
          })}
        </div>
      </nav>
    </>
  );
}
